/**
 * VoiceCard Component
 *
 * Displays a cloned voice card with preview player,
 * settings, and action buttons.
 */

import { useCallback } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings, Trash2, Mic, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { VoicePreviewPlayer } from './VoicePreviewPlayer';
import type { IVoiceCardProps } from '@/@types/voices';
import { cn } from '@/lib/utils';

/**
 * Format date relative to now
 */
function formatRelativeDate(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 30) {
    return new Date(timestamp).toLocaleDateString();
  }
  if (days > 0) {
    return `${days}d ago`;
  }
  if (hours > 0) {
    return `${hours}h ago`;
  }
  if (minutes > 0) {
    return `${minutes}m ago`;
  }
  return 'Just now';
}

/**
 * Get status badge variant and icon
 */
function getStatusInfo(status: string): {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: React.ReactNode;
  label: string;
} {
  switch (status) {
    case 'active':
      return {
        variant: 'default',
        icon: <CheckCircle className="w-3 h-3" />,
        label: 'Active',
      };
    case 'creating':
      return {
        variant: 'secondary',
        icon: <Clock className="w-3 h-3 animate-pulse" />,
        label: 'Creating...',
      };
    case 'failed':
      return {
        variant: 'destructive',
        icon: <AlertCircle className="w-3 h-3" />,
        label: 'Failed',
      };
    default:
      return {
        variant: 'outline',
        icon: null,
        label: status,
      };
  }
}

function VoiceCard({
  voice,
  isSelected,
  onSelect,
  onDelete,
  onEditSettings,
  onRefreshPreview,
  isRefreshingPreview,
}: IVoiceCardProps) {
  const handleSelect = useCallback(() => {
    onSelect?.(voice._id);
  }, [onSelect, voice._id]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(voice._id);
    },
    [onDelete, voice._id]
  );

  const handleEditSettings = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEditSettings?.(voice._id);
    },
    [onEditSettings, voice._id]
  );

  const handleRefreshPreview = useCallback(async () => {
    await onRefreshPreview?.(voice._id);
  }, [onRefreshPreview, voice._id]);

  const statusInfo = getStatusInfo(voice.status);
  const isActive = voice.status === 'active';

  return (
    <Card
      className={cn(
        'overflow-hidden hover:shadow-md transition-all cursor-pointer',
        isSelected && 'ring-2 ring-blue-500',
        !isActive && 'opacity-75'
      )}
      onClick={handleSelect}
    >
      {/* Header with Voice Icon */}
      <div className="relative bg-gradient-to-br from-blue-950/30 to-indigo-950/30 light:from-blue-50 light:to-indigo-50 p-6">
        <div className="flex items-center justify-center">
          <div className="p-4 bg-card rounded-full shadow-sm">
            <Mic className="w-8 h-8 text-blue-400 light:text-blue-600" />
          </div>
        </div>

        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <Badge variant={statusInfo.variant} className="flex items-center gap-1">
            {statusInfo.icon}
            {statusInfo.label}
          </Badge>
        </div>

        {/* Selected Indicator */}
        {isSelected && (
          <div className="absolute top-3 left-3">
            <div className="p-1 bg-blue-500 rounded-full">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="text-base truncate">{voice.name}</CardTitle>
        {voice.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{voice.description}</p>
        )}
      </CardHeader>

      <CardContent className="py-0 space-y-2">
        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Created {formatRelativeDate(voice.createdAt)}</span>
          <span>
            {voice.sampleCount} sample{voice.sampleCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Usage Count */}
        {voice.usageCount > 0 && (
          <div className="text-xs text-muted-foreground">
            Used {voice.usageCount} time{voice.usageCount !== 1 ? 's' : ''}
          </div>
        )}

        {/* Error Message */}
        {voice.status === 'failed' && voice.errorMessage && (
          <div className="p-2 bg-red-950/30 light:bg-red-50 rounded text-xs text-red-400 light:text-red-600">
            {voice.errorMessage}
          </div>
        )}

        {/* Preview Player */}
        {isActive && (
          <div
            role="presentation"
            className="pt-2"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <VoicePreviewPlayer
              previewUrl={voice.previewUrl}
              voiceName={voice.name}
              onGeneratePreview={onRefreshPreview ? handleRefreshPreview : undefined}
              isGeneratingPreview={isRefreshingPreview}
            />
          </div>
        )}
      </CardContent>

      <CardFooter className="gap-2 pt-4">
        {onEditSettings && isActive && (
          <Button variant="outline" size="sm" onClick={handleEditSettings} className="flex-1">
            <Settings className="w-3.5 h-3.5 mr-1.5" />
            Settings
          </Button>
        )}
        {onDelete && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            className="px-3 text-red-500 hover:text-red-600 hover:bg-red-950/30 light:hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

export { VoiceCard };
